import React, { useState, useContext } from "react";
import { ColorValue, Image, Platform, StyleSheet, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import Icon from "react-native-vector-icons/Ionicons";
import { PlaceColors } from "../helpers/PlaceColors";
import { FindifyDataContext, FindifyDataContextType } from "@/contexts/FindifyDataContext";
import { PlaceText } from "./PlaceText";
import { DefaultShadow, TextShadow, IconShadow } from "../helpers/DefaultShadows";
import { FindifyMenuContext, FindifyMenuContextType } from "@/contexts/FindifyMenuContext";
import { toggleSidemenu } from "./FindifyNavigation";
import Config from "react-native-config";


export type SimpleHeaderAction = {
    action: Function,
    icon: string,
    color?: number | ColorValue,
}
type SimpleHeaderArgs = {
    navigation: any,
    title?: string,
    subtitle?: string,
    actions?: SimpleHeaderAction[],
    logo?: string,
    image?: string,
    isEventLayout?: boolean,
    hideMenu?: boolean,
    hideGoBack?: boolean,
    backgroundColor?: ColorValue,
    changePlaceLeft?: boolean,
    children?: JSX.Element // Should be absolute positioned
}


const leftSideIcon = (hideGoBack: boolean, navigation: any, changePlaceLeft: boolean, findifyDataContext: FindifyDataContextType, findifyMenuContext: FindifyMenuContextType, color?: number | ColorValue) => {
    const wrapIcon = (child: JSX.Element) => <View style={{ backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: 24, margin: 8, padding: 2 }}>{child}</View>;
    const canOpenFullMenu = findifyMenuContext?.isSidemenu
        || (Number(findifyDataContext?.workspace?.workspace_id) === 156 && (findifyDataContext?.workspace?.app_settings?.app_menu_lines?.length ?? 0) > 4);

    if (canOpenFullMenu) {
        return wrapIcon(<Icon
            key="sidemenu"
            style={[styles.headerIcon]}
            name="menu-outline"
            size={30}
            color={"white"}
            onPress={() => toggleSidemenu(findifyMenuContext)} />)
    } else if (changePlaceLeft) {
        if (Config.WORKSPACE_ID) return <></>
        return wrapIcon(<Icon key="back" style={[styles.headerIcon]} name="chevron-back" size={30} color={"white"} onPress={() => {
            findifyDataContext?.setVisitWorkspaceSelected(false)
        }} />);
    } else if (!hideGoBack && navigation?.canGoBack()) {
        return wrapIcon(<Icon key="back" style={[styles.headerIcon]} name="chevron-back" size={30} color={"white"} onPress={() => {
            navigation?.goBack();
        }} />);
    }

    return <></>
}


const makeHeaderIcon = (action: SimpleHeaderAction, color?: number | ColorValue) => {
    return <View key={action.icon} style={{ backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: 24, marginVertical: 8, marginRight: 8, padding: 2 }}>
        <Icon
            style={[styles.headerIcon]}
            name={action.icon}
            size={30}
            color={action.color ?? "white"}
            onPress={() => action.action()}
        />
    </View>
}

const makeHeaderActionsIcons = (actions?: SimpleHeaderAction[], color?: number | ColorValue) => {
    let icons: JSX.Element[] = [];
    if (actions && actions.length > 0) {
        actions.map((action: SimpleHeaderAction) => {
            icons.push(makeHeaderIcon(action, color));
        });
    }
    return icons;
}

const rightSideIcon = (hideGoBack: boolean, navigation: any, findifyMenuContext: FindifyMenuContextType, color?: number | ColorValue, actions?: SimpleHeaderAction[]) => {
    let icons: JSX.Element[] = [];
    if (findifyMenuContext?.isSidemenu) {
        if (!hideGoBack && navigation?.canGoBack()) {
            icons.push(makeHeaderIcon({ icon: 'chevron-back', action: () => navigation?.goBack(), }, color));
        }
    }
    icons.push(...makeHeaderActionsIcons(actions, color));
    return icons;
}

export const FindifySimpleHeader = ({
    navigation,
    title,
    subtitle,
    actions,
    logo,
    image,
    hideMenu,
    hideGoBack,
    children,
    isEventLayout,
    changePlaceLeft,
    backgroundColor,
}: SimpleHeaderArgs) => {
    const insets = useSafeAreaInsets();
    const [dialogCode] = useState<React.JSX.Element | null>(null);
    const findifyDataContext = useContext(FindifyDataContext);
    const findifyMenuContext = useContext(FindifyMenuContext);

    if (image || logo) {
        if (isEventLayout) {
            return <View style={{
                width: '100%',
                zIndex: 1
            }}>
                <View
                    style={[{
                        height: 220,
                        backgroundColor: backgroundColor ?? PlaceColors(findifyDataContext).background.get(),
                        borderBottomLeftRadius: 36,
                        borderBottomRightRadius: 36,
                        overflow: 'hidden',
                    }, DefaultShadow]}
                >
                    <Image style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: -10 }} source={{ uri: image }} />
                    <View style={{ position: 'absolute', bottom: 0, width: '100%', height: 120, backgroundColor: 'rgba(0,0,0,0.4)', borderBottomLeftRadius: 36, borderBottomRightRadius: 36 }} />
                    <View style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 10,
                        alignItems: 'center',
                        top: 60,
                        zIndex: 2,
                    }}>
                        <View style={{
                            flex: 1,
                            flexDirection: 'column',
                            marginLeft: 10,
                        }}>
                            <PlaceText style={
                                [
                                    TextShadow,
                                    {
                                        // flex: 1,
                                        width: '100%',
                                        color: "white",
                                        fontSize: 34,
                                        fontWeight: 'bold',
                                    },
                                ]
                            }>{title}</PlaceText>
                            <PlaceText style={
                                [
                                    TextShadow,
                                    {
                                        color: "white",
                                        fontSize: 16,
                                        fontWeight: '500',
                                        opacity: 0.9,
                                    },
                                ]
                            }>{subtitle}</PlaceText>
                        </View>
                        <Image style={{
                            borderRadius: 100,
                            borderColor: 'white',
                            borderWidth: 2,
                            width: 120,
                            height: 120,
                            marginRight: 20,
                            objectFit: 'cover',
                            ...IconShadow
                        }} source={{ uri: logo }} />
                    </View>
                    <View style={{ position: 'absolute', top: insets.top, left: 0, zIndex: 10 }}>
                        {leftSideIcon(hideGoBack, navigation, changePlaceLeft, findifyDataContext, findifyMenuContext, "white")}
                    </View>
                    <View
                        style={{
                            position: 'absolute',
                            top: insets.top,
                            right: 0,
                            flexDirection: 'row',
                            zIndex: 10,
                        }}
                    >
                        {rightSideIcon(hideGoBack, navigation, findifyMenuContext, "white", actions,)}
                    </View>
                    {dialogCode != null && dialogCode}
                </View>
                {children}
            </View>
        } else {
            if (image) {
                return <View style={{
                    width: '100%',
                    zIndex: 1
                }}>
                    <View
                        style={[{
                            height: 250,
                            backgroundColor: backgroundColor ?? PlaceColors(findifyDataContext).background.get(),
                            borderBottomLeftRadius: 36,
                            borderBottomRightRadius: 36,
                            overflow: 'hidden',
                        }, DefaultShadow]}
                    >
                        <Image style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} source={{ uri: image }} />
                        {/* Gradient Overlay approximation for text readability */}
                        <View style={{ position: 'absolute', bottom: 0, width: '100%', height: 120, backgroundColor: 'rgba(0,0,0,0.4)', borderBottomLeftRadius: 36, borderBottomRightRadius: 36 }} />
                        <View style={{ position: 'absolute', top: insets.top, left: 0, zIndex: 10 }}>
                            {leftSideIcon(hideGoBack, navigation, changePlaceLeft, findifyDataContext, findifyMenuContext, "white")}
                        </View>
                        <View
                            style={{
                                position: 'absolute',
                                top: insets.top,
                                right: 0,
                                flexDirection: 'row',
                                zIndex: 10,
                            }}
                        >
                            {rightSideIcon(hideGoBack, navigation, findifyMenuContext, "white", actions,)}
                        </View>
                        {dialogCode != null && dialogCode}
                        <View style={{
                            position: 'absolute',
                            bottom: 10,
                            left: 10,
                            width: '85%',
                        }}
                        >

                            <PlaceText style={
                                [
                                    TextShadow,
                                    {
                                        color: "white",
                                        fontSize: 26,
                                        fontWeight: 'bold',
                                    },
                                ]
                            }>{title}</PlaceText>
                            <PlaceText style={
                                [
                                    TextShadow,
                                    {
                                        color: "white",
                                        fontSize: 16,
                                        fontWeight: '500',
                                        opacity: 0.9,
                                    },
                                ]
                            }>{subtitle}</PlaceText>
                        </View>
                    </View>
                    {children}
                </View>
            }
            else {
                return <MakeNoImageHeader
                    isEventLayout={isEventLayout}
                    children={children}
                    title={title}
                    hideGoBack={hideGoBack}
                    navigation={navigation}
                    changePlaceLeft={changePlaceLeft}
                    actions={actions}
                    dialogCode={dialogCode}
                    findifyMenuContext={findifyMenuContext}
                    findifyDataContext={findifyDataContext}
                    insets={insets}
                    backgroundColor={backgroundColor}
                />
            }
        }
    } else {
        return <MakeNoImageHeader
            isEventLayout={isEventLayout}
            children={children}
            title={title}
            hideMenu={hideMenu}
            hideGoBack={hideGoBack}
            navigation={navigation}
            changePlaceLeft={changePlaceLeft}
            actions={actions}
            dialogCode={dialogCode}
            findifyMenuContext={findifyMenuContext}
            findifyDataContext={findifyDataContext}
            insets={insets}
            backgroundColor={backgroundColor}
        />
    }
}


const MakeNoImageHeader = ({
    isEventLayout,
    children,
    title,
    hideMenu,
    hideGoBack,
    navigation,
    changePlaceLeft,
    actions,
    dialogCode,
    findifyDataContext,
    findifyMenuContext,
    insets,
    backgroundColor
}: any) => {

    return <View style={{
        width: '100%',
        zIndex: 1
    }}>
        <View
            style={[{
                backgroundColor: backgroundColor ?? PlaceColors(findifyDataContext).background.get(),
            },
            isEventLayout ? {} : DefaultShadow
            ]}
        >
            <View style={{
                display: 'flex',
                flexDirection: 'row',
                width: '100%',
                marginTop: insets.top,
                minHeight: 40,
                justifyContent: 'center'
            }}>
                <View style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 4,
                    marginLeft: hideMenu ? 10 : 0,
                }}>
                    {!hideMenu &&
                        leftSideIcon(hideGoBack, navigation, changePlaceLeft, findifyDataContext, findifyMenuContext, PlaceColors(findifyDataContext).primary.get())
                    }
                </View>
                <View style={{ flex: 1 }}>
                    {!!title && <PlaceText style={
                        [
                            TextShadow,
                            {
                                width: 'auto',
                                color: PlaceColors(findifyDataContext).primary.get(),
                                marginTop: Platform.OS === 'ios' ? 5 : 2,
                                fontSize: 24,
                                fontWeight: 'bold',
                                marginLeft: 0,
                                bottom: undefined,
                            },
                        ]
                    }>{title}</PlaceText>}
                </View>
                <View
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 4,
                    }}
                >
                    {rightSideIcon(hideGoBack, navigation, findifyMenuContext, PlaceColors(findifyDataContext).primary.get(), actions,)}
                </View>
            </View>
            {dialogCode != null && dialogCode}
        </View>
        {children}
    </View >;
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    headerIcon: {
        padding: 5,
        ...IconShadow
    }
});
